import React, { useState, useEffect } from "react";
import { Trash2, ShieldCheck, Loader2, CheckCircle2, ArrowLeft, AlertTriangle } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const TITLE = "Delete Paras CashBack Account";
const DESC = "Request deletion of your Paras CashBack account and learn about account data, transaction records and applicable retention requirements.";

export default function DeleteAccount() {
  const [form, setForm] = useState({ email: "", mobile: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    const prev = document.title;
    document.title = TITLE;
    const m = document.querySelector('meta[name="description"]');
    const prevDesc = m ? m.getAttribute("content") : null;
    if (m) m.setAttribute("content", DESC);
    return () => { document.title = prev; if (m && prevDesc) m.setAttribute("content", prevDesc); };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() && !form.mobile.trim()) {
      setErr("Please enter the registered email or mobile number linked to your account.");
      return;
    }
    setErr(""); setBusy(true);
    try {
      const r = await fetch(`${BACKEND}/api/public/account/deletion-request`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Request failed");
      setDone(data);
    } catch (e) { setErr(e.message || "Something went wrong. Please try again."); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="delete-account-page">
      <div className="mx-auto max-w-2xl px-5 py-10">
        <a href="/" className="mb-8 inline-flex items-center gap-2 text-sm text-slate-400 hover:text-emerald-400" data-testid="back-home-link">
          <ArrowLeft className="h-4 w-4" /> Back to Paras CashBack
        </a>

        <div className="flex items-center gap-3">
          <div className="rounded-2xl bg-rose-500/15 p-3"><Trash2 className="h-6 w-6 text-rose-400" /></div>
          <div>
            <h1 className="font-heading text-3xl font-extrabold">Delete Your Account</h1>
            <p className="text-sm text-slate-400">Paras CashBack · account & data deletion</p>
          </div>
        </div>

        <p className="mt-6 text-sm leading-relaxed text-slate-300">
          You may request deletion of your Paras CashBack account through the available account
          deletion process or by contacting support.
        </p>

        <div className="mt-8 space-y-6 text-sm leading-relaxed text-slate-300">
          <section className="rounded-2xl border border-amber-500/20 bg-amber-500/5 p-5">
            <div className="mb-2 flex items-center gap-2 text-slate-100"><AlertTriangle className="h-4 w-4 text-amber-400" /><h2 className="font-semibold">Before deleting your account</h2></div>
            <p className="mb-2 text-slate-400">Please note that:</p>
            <ul className="list-disc space-y-1.5 pl-5 text-slate-400">
              <li>Pending transactions may need to be completed or reviewed.</li>
              <li>Certain transaction and financial records may need to be retained where required by law or legitimate business obligations.</li>
              <li>Account deletion may not immediately remove records that are subject to legal, security, fraud-prevention or financial-record retention requirements.</li>
            </ul>
          </section>

          <section className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5">
            <h2 className="mb-2 font-semibold text-slate-100">Account deletion request</h2>
            <p className="mb-2 text-slate-400">Please provide:</p>
            <ul className="list-disc space-y-1.5 pl-5 text-slate-400">
              <li>Registered mobile number</li>
              <li>Registered email address, if applicable</li>
              <li>Account identification information required for verification</li>
            </ul>
          </section>

          <section className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5">
            <div className="mb-2 flex items-center gap-2 text-slate-100"><ShieldCheck className="h-4 w-4 text-emerald-400" /><h2 className="font-semibold">Security</h2></div>
            <p className="text-slate-400">We may verify account ownership before processing a deletion request.</p>
          </section>

          <section>
            <h2 className="mb-2 font-semibold text-slate-100">After deletion</h2>
            <p className="text-slate-400">
              Your account access may be disabled and personal information will be handled according
              to the Paras CashBack Privacy Policy and applicable legal retention requirements.
            </p>
          </section>
        </div>

        {done ? (
          <div className="mt-8 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-6 text-center" data-testid="deletion-success">
            <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-400" />
            <p className="mt-3 font-semibold text-emerald-300">Request received</p>
            <p className="mt-1 text-sm text-slate-300">{done.message}</p>
            <p className="mt-2 font-mono text-xs text-slate-500">Reference: {done.request_id}</p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-4 rounded-2xl border border-slate-800 bg-slate-900/50 p-6">
            <div>
              <label className="font-mono text-xs uppercase tracking-widest text-slate-500">Registered mobile number</label>
              <input data-testid="delete-mobile" value={form.mobile}
                onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                placeholder="10-digit number"
                className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950/60 px-3 py-2.5 text-sm outline-none focus:border-emerald-500" />
            </div>
            <div>
              <label className="font-mono text-xs uppercase tracking-widest text-slate-500">Registered email address (if applicable)</label>
              <input data-testid="delete-email" type="email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="you@example.com"
                className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950/60 px-3 py-2.5 text-sm outline-none focus:border-emerald-500" />
            </div>
            <div>
              <label className="font-mono text-xs uppercase tracking-widest text-slate-500">Reason (optional)</label>
              <textarea data-testid="delete-reason" value={form.reason} rows={3}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950/60 px-3 py-2.5 text-sm outline-none focus:border-emerald-500" />
            </div>
            {err && <p className="text-sm text-rose-400" data-testid="delete-error">{err}</p>}
            <button data-testid="delete-submit" type="submit" disabled={busy}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-rose-500 py-3 font-semibold text-white hover:bg-rose-400 disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Request Account Deletion
            </button>
          </form>
        )}

        <p className="mt-8 text-center text-xs text-slate-600">
          Questions? Email info@parasreward.com · Paras CashBack
        </p>
      </div>
    </div>
  );
}
